/**
 * Burrs, the client's half -- and the one definition of "in a burr bush"
 * that both halves share.
 *
 * The split of responsibility is the protocol header's: the CLIENT shows the
 * tangle (player.js damps the cat's own movement, which only ever makes it
 * move LESS, so nothing here needs trusting), and the SERVER judges the
 * sting (server/src/burrs.js, from the pose it accepted).  For that split to
 * be fair the two ends must agree exactly on where the bushes are and what
 * counts as standing in one -- so the positions come off the level build
 * both sides already run (the "burr:" markers greenspace.js's burrBush
 * plants; there is no second list anywhere), and the geometry test lives
 * here, in the one module both sides import.  A number copied instead of
 * imported would be a cat snagged on one screen and free on another.
 *
 * DOM only enters through BurrCue at the bottom, and only when constructed
 * -- the server imports the pure functions above it the same way world.js
 * imports the Player.
 *
 * WS-06 cut the file on exactly that line: BurrCue, the DOM twin, lives at
 * packages/client/src/burrs.js now (docs/ARCH-PLAN.md §1.2's standing rule).
 */
import { BURR_R, BURR_Y_UP, BURR_Y_DOWN } from "@catworld/shared";

/** Every burred bush the level built: the "burr:" markers, as plain spots.
    The name rides along for tests and tools; nothing in play reads it. */
export function burrList(markers) {
  const out = [];
  for (const [name, m] of Object.entries(markers ?? {})) {
    if (name.startsWith("burr:")) {
      out.push({ name: name.slice(5), x: m.x, y: m.y, z: m.z });
    }
  }
  return out;
}

/**
 * The snare: (x, y, z) -> the bush those paws are tangled in, or null.
 *
 * A paw position is in a bush when it is inside the BURR_R disc in plan AND
 * inside the height band around the bush's own ground -- above by less than
 * BURR_Y_UP (a leaping cat clears the plant; constants.js has the jump
 * arithmetic) and below by less than BURR_Y_DOWN (a paw judged from a dip
 * beside the base still counts; the creek bed below that does not).
 *
 * `slack` widens the disc for one caller: player.js asks "am I still in the
 * bush I am tangled in?" with a little margin, so that a teleport (respawn,
 * taking the seat of a wandered cat) releases the tangle instantly instead
 * of carrying two seconds of stuck to a spot with no bush in sight, while
 * the wriggle's own creep -- capped far below BURR_R per tangle -- never
 * reaches the widened rim and can never self-release.
 *
 * Nine bushes, a linear scan: this runs a handful of times a frame against
 * a list the level fixed at build time, and a spatial index would be more
 * code than the data.
 */
export function burrSnare(markers) {
  const list = burrList(markers);
  return (x, y, z, slack = 0) => {
    const r = BURR_R + slack;
    for (const bush of list) {
      const dy = y - bush.y;
      if (dy > BURR_Y_UP || dy < -BURR_Y_DOWN) continue;
      if ((bush.x - x) ** 2 + (bush.z - z) ** 2 <= r * r) return bush;
    }
    return null;
  };
}

/**
 * How often the tangle's wriggle pose bobs, seconds per cycle, and how much
 * of each cycle is spent in the crouch.  Read by player.js's resolveState:
 * the struggle is drawn by cycling two clips every cat already ships --
 * `land` (the pressed-low crouch) and `idle` -- rather than by a new
 * animation state, so every client on the street renders another cat's
 * tangle exactly as its own, and a snapshot from this build plays fine on
 * one from before burrs existed.  0.55 s reads as effortful rather than
 * twitchy, and the phase runs off the tangle clock itself, so mashing --
 * which eats that clock in SNAG_MASH bites -- visibly redoubles the
 * wriggling.  That feedback loop is the whole reason these are not buried
 * as literals in resolveState.
 */
export const SNAG_BOB = 0.55;
export const SNAG_BOB_DOWN = 0.3;
